/**
 * Fetches a federated-package manifest from a CDN URL and returns the
 * versioned remoteEntry URL to load.
 *
 * The manifest pattern solves the `/latest/` cache-invalidation problem:
 * bundle paths are immutable per-deploy (`/abax-ui/{IID}/remoteEntry.js`) and
 * the manifest is the short-cache pointer that clients revalidate every boot.
 *
 * Shape (matches packages/ui/.gitlab-ci.yml deploy job output):
 * ```json
 * {
 *   "version": "0.0.0-<sha>",
 *   "pipelineId": 2669912,
 *   "remoteEntry": "https://.../abax-ui/2669912/remoteEntry.js",
 *   "builtAt": "2026-04-17T10:23:00Z",
 *   "commit": "<full-sha>"
 * }
 * ```
 */

import { setResolvedIntegrity } from './preload-registry';
import { validateRemoteUrl } from './validate-url';

/**
 * Tagged error class for manifest tamper signals. `resolveRemoteUrl` propagates
 * these instead of falling back to the legacy URL - a tamper signal is the
 * exact event that should *prevent* loading the remote, not be converted into
 * an unpinned legacy load.
 *
 * Thrown for: commit-pin mismatch, missing pin in fail-closed env, malformed
 * integrity, disallowed `remoteEntry` URL.
 *
 * NOT thrown for retryable / non-tamper failures (network errors, timeouts,
 * 5xx, JSON parse, missing optional fields) - those legitimately fall back.
 */
export class ManifestTamperError extends Error {
  readonly kind = 'manifest-tamper';

  constructor(message: string) {
    super(message);
    this.name = 'ManifestTamperError';
  }
}

/**
 * Tagged error class for HTTP-level manifest fetch failures. Carries the
 * raw `status` so `resolveRemoteUrl` can split the observability response:
 * 4xx is an operator-actionable misconfig (manifest path moved, ACL drift,
 * env-config typo) and warrants `console.error` so RUM/log tooling captures
 * it; 5xx / network / timeout are transient and stay at `console.warn`.
 * Both paths still fall back to the legacy URL - only the log level differs.
 */
export class ManifestHttpError extends Error {
  readonly kind = 'manifest-http';

  readonly status: number;

  constructor(status: number, url: string) {
    super(`[manifest] HTTP ${status} for ${url}`);
    this.name = 'ManifestHttpError';
    this.status = status;
  }
}

// SRI hash format: `sha384-{base64}` (also accepts sha256/sha512 prefixes).
// Validated before propagation so a malformed manifest field can't reach the
// `<script integrity=...>` attribute as raw garbage. The previous regex
// `[A-Za-z0-9+/=]+` permitted `=` anywhere in the body; base64 padding is
// strictly trailing (0-2 chars) - a malformed-but-non-injectable input would
// otherwise slip through into the DOM and the browser would reject SRI
// silently (load failure surfaces as "integrity mismatch", not validation).
const INTEGRITY_RE = /^sha(256|384|512)-[A-Za-z0-9+/]+={0,2}$/;

export interface PackageManifest {
  version: string;
  pipelineId: number;
  remoteEntry: string;
  /**
   * ISO timestamp the CI manifest emitter wrote. Producer always sets it,
   * but `fetchManifest` does not validate the field and no consumer in
   * `src/` reads it - typed optional so the runtime/typing surface matches.
   * Future audit/SBOM consumers should validate at the read site.
   */
  builtAt?: string;
  commit: string;
  /**
   * Optional SRI hash for the remoteEntry.js bundle, in
   * `sha{256,384,512}-{base64}` form. CI emits sha384. When present and
   * well-formed, the shell sets `<script integrity=...>` so a tampered CDN
   * cannot ship arbitrary JS even if the host validation passes.
   */
  integrity?: string;
}

export interface FetchManifestOptions {
  /** Override the per-fetch timeout (ms). Default is `DEFAULT_TIMEOUT_MS`. */
  timeoutMs?: number;
  /**
   * If set, the fetched manifest must report this commit SHA in its `commit`
   * field. A mismatch throws - defense-in-depth against a compromised CDN
   * bucket swapping the manifest with attacker-controlled remoteEntry URLs.
   * When unset (typical dev / unpinned-deploy), the check is skipped and a
   * single console warning surfaces so operators see the gap.
   */
  expectedCommit?: string;
  /**
   * When `true`, missing `expectedCommit` becomes a hard error (rather than
   * a single console warning). The shell sets this in production builds so
   * a forgotten `K8S_SECRET_ABAX_UI_EXPECTED_COMMIT` fails the pod boot
   * instead of silently shipping without manifest pinning.
   */
  requireCommitPin?: boolean;
}

// Tightened from 5000ms after review - Cloudflare REVALIDATED 304 round-trip
// to GCS is typically <500ms. A 5s wait before falling back to legacy was
// user-perceived boot delay on every page load when the manifest origin is
// degraded. 2.5s is generous enough for transcontinental cold-cache fills.
//
// Callers are expected to override via `opts.timeoutMs` when consuming a
// runtime config knob (e.g. env-config.json `ABAX_UI_MANIFEST_TIMEOUT_MS`).
const DEFAULT_TIMEOUT_MS = 2500;

let warnedAboutMissingPin = false;

export async function fetchManifest(
  url: string,
  opts: FetchManifestOptions = {},
): Promise<PackageManifest> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      // 'no-cache' forces revalidation on every request but still allows 304
      // short-circuit (empty body) when the ETag matches. 'no-store' would
      // re-download the full body every boot, defeating Cloudflare's
      // must-revalidate round-trip optimization.
      cache: 'no-cache',
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!res.ok) {
      throw new ManifestHttpError(res.status, url);
    }
    let json: Partial<PackageManifest>;
    try {
      json = (await res.json()) as Partial<PackageManifest>;
    } catch (err) {
      throw new Error(
        `[manifest] invalid JSON from ${url}: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
    if (typeof json?.remoteEntry !== 'string' || json.remoteEntry.length === 0) {
      throw new Error(`[manifest] missing or invalid remoteEntry in ${url}`);
    }
    // Defense-in-depth: even when the manifest fetch came from an https origin,
    // the `remoteEntry` field is whatever the manifest emitter wrote into the
    // JSON. A compromised CDN bucket or upstream pipeline could embed an
    // attacker-controlled URL and the script tag would still load it. Reject
    // here so the bad payload surfaces at fetch time rather than at script
    // injection (CWE-829).
    if (!validateRemoteUrl(json.remoteEntry)) {
      // Tamper signal: a compromised CDN bucket served a remoteEntry URL
      // outside the allowlist. Must not silently fall back to the legacy
      // URL - propagate so the loader fails closed.
      throw new ManifestTamperError(
        `[manifest] remoteEntry failed https validation in ${url}: ${json.remoteEntry}`,
      );
    }
    if (typeof json?.version !== 'string') {
      throw new Error(`[manifest] missing or invalid version in ${url}`);
    }
    if (typeof json?.pipelineId !== 'number') {
      throw new Error(`[manifest] missing or invalid pipelineId in ${url}`);
    }
    if (typeof json?.commit !== 'string' || json.commit.length === 0) {
      throw new Error(`[manifest] missing or invalid commit in ${url}`);
    }
    // Optional - older manifests pre-CI-rollout don't carry it. Validate
    // the format when present so a malformed value can't slip through to
    // the script.integrity attribute as raw garbage.
    if (json.integrity !== undefined) {
      if (typeof json.integrity !== 'string' || !INTEGRITY_RE.test(json.integrity)) {
        // Tamper signal: malformed `integrity` cannot land in the DOM as
        // raw garbage; propagate fail-closed.
        throw new ManifestTamperError(
          `[manifest] integrity field must match sha{256,384,512}-{base64}, got ${
            typeof json.integrity === 'string' ? json.integrity : typeof json.integrity
          }`,
        );
      }
    }
    if (opts.expectedCommit) {
      if (json.commit !== opts.expectedCommit) {
        // Tamper signal: commit pin mismatch is the exact event the pin
        // exists to detect. Must NOT fall back to an unpinned legacy URL.
        throw new ManifestTamperError(
          `[manifest] commit pin mismatch for ${url}: expected ${opts.expectedCommit}, got ${json.commit}`,
        );
      }
    } else if (opts.requireCommitPin) {
      // Production / staging deploy with no pin set is treated as a hard
      // failure so a forgotten K8S_SECRET_ABAX_UI_EXPECTED_COMMIT does not
      // silently disable the manifest-tamper defense.
      throw new ManifestTamperError(
        `[manifest] expectedCommit pin is required in this environment — set K8S_SECRET_ABAX_UI_EXPECTED_COMMIT to the deployed SHA before boot`,
      );
    } else if (!warnedAboutMissingPin) {
      // eslint-disable-next-line no-console
      console.warn(
        `[manifest] no expectedCommit pin supplied — set env-config.json ABAX_UI_EXPECTED_COMMIT to enable defense-in-depth`,
      );
      warnedAboutMissingPin = true;
    }
    return json as PackageManifest;
  } finally {
    clearTimeout(timer);
  }
}

export interface ResolveRemoteUrlOptions {
  /** Manifest URL - takes precedence when set. */
  manifestUrl?: string;
  /** Direct remoteEntry.js URL - used as fallback or when no manifest. */
  fallbackUrl?: string;
  /**
   * Expected commit SHA for the resolved manifest. If set, fetch verifies
   * `manifest.commit === expectedCommit` and rejects on mismatch. Surfaces
   * the bad payload at fetch time rather than at script injection.
   */
  expectedCommit?: string;
  /** Override the manifest fetch timeout. */
  timeoutMs?: number;
  /**
   * Fail-closed when the pin is missing (production environments). Forwarded
   * to `fetchManifest` so a forgotten env var fails boot rather than silently
   * disabling tamper-detection.
   */
  requireCommitPin?: boolean;
}

/**
 * Resolves a federated package's remoteEntry URL.
 *
 * - With manifestUrl set: fetches manifest, returns its remoteEntry field.
 * - On manifest failure (HTTP error, timeout, malformed JSON, missing/invalid
 *   fields, non-https manifest URL): falls back to fallbackUrl with a warning
 *   log. Boot is never blocked by manifest.
 * - With no manifestUrl: returns fallbackUrl directly.
 *
 * Returns null if neither path yields a usable URL.
 *
 * `label` appears in console messages - use the package name (e.g. "abax-ui").
 */
export async function resolveRemoteUrl(
  opts: ResolveRemoteUrlOptions,
  label: string,
): Promise<string | null> {
  if (opts.manifestUrl) {
    // Defense-in-depth: reject non-https manifest URLs before fetch. Prevents
    // compromised env-config from redirecting the manifest fetch to an
    // attacker-controlled host. Mirrors the check applied to remoteEntry URLs
    // downstream. Localhost allowed for dev (matches validateRemoteUrl).
    if (!validateRemoteUrl(opts.manifestUrl)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[${label}] manifest URL failed https validation, falling back: ${opts.manifestUrl}`,
      );
      return opts.fallbackUrl || null;
    }
    const started = Date.now();
    try {
      const manifest = await fetchManifest(opts.manifestUrl, {
        timeoutMs: opts.timeoutMs,
        expectedCommit: opts.expectedCommit,
        requireCommitPin: opts.requireCommitPin,
      });
      // Forward optional SRI hash to the load-time script.integrity attribute
      // via the preload registry (side-channel keyed on package label so
      // existing string-only callers keep working unchanged).
      if (manifest.integrity) {
        setResolvedIntegrity(label, manifest.integrity);
      }
      // eslint-disable-next-line no-console
      console.info(
        `[${label}] resolved via manifest: ${manifest.version} (pipeline ${manifest.pipelineId}, ${
          Date.now() - started
        }ms)`,
      );
      return manifest.remoteEntry;
    } catch (err) {
      // Tamper signals MUST NOT silently fall back to the unpinned legacy
      // URL - the whole point of the pin/SRI/allowlist chain is to detect
      // exactly this case. Re-throw so `preloadAbaxUi`'s catch flips
      // `__ABAX_UI_LOAD_FAILED__` instead of installing the legacy bundle
      // without manifest verification.
      if (err instanceof ManifestTamperError) {
        // eslint-disable-next-line no-console
        console.error(`[${label}] manifest tamper signal — refusing legacy fallback`, {
          url: opts.manifestUrl,
          durationMs: Date.now() - started,
          err,
        });
        throw err;
      }
      // 4xx is an operator-actionable misconfig (manifest path moved,
      // ACL drift, env-config typo, CDN rule change) - silent warn-only
      // fallback hides that tamper detection is now permanently disabled
      // for the rest of this pod's lifetime. Surface at error level so
      // RUM / log tooling that captures `console.error` picks it up.
      // 5xx / network / timeout / parse / missing-optional remain at
      // warn level because they're transient and not operator-actionable.
      // Both paths still fall back to the legacy URL - only the log level
      // differs.
      const isClientError =
        err instanceof ManifestHttpError && err.status >= 400 && err.status < 500;
      const logFn = isClientError ? console.error : console.warn;
      // eslint-disable-next-line no-console
      logFn(`[${label}] manifest fetch failed, falling back to legacy URL`, {
        url: opts.manifestUrl,
        durationMs: Date.now() - started,
        err,
      });
    }
  }
  return opts.fallbackUrl || null;
}

/**
 * Test hook - resets the module-scoped `warnedAboutMissingPin` flag so
 * unit specs asserting the warn-once behavior can re-trigger it across
 * cases without relying on `jest.resetModules()` (which also nukes the
 * preload-registry singletons).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function __internal_resetWarnedAboutMissingPinForTests(): void {
  warnedAboutMissingPin = false;
}

// The preload-promise registry moved to its own module
// (`preload-registry.ts`) - re-exported here for back-compat during the
// gradual import-site migration. Update import sites, then drop these
// re-exports in a follow-up.
//
// Each re-export is `@deprecated` so IDEs surface the migration target at
// the call site and a routine grep is sufficient to find the last hold-outs.
/**
 * @deprecated Import directly from `./preload-registry`. This re-export
 *   exists solely for back-compat during the import-site migration and will
 *   be removed in a follow-up.
 */
export { setPreloadPromise } from './preload-registry';
/**
 * @deprecated Import directly from `./preload-registry`. This re-export
 *   exists solely for back-compat during the import-site migration and will
 *   be removed in a follow-up.
 */
export { getResolvedUrl } from './preload-registry';
/**
 * @deprecated Import `__internal_resetPreloadPromisesForTests` directly
 *   from `./preload-registry`. This aliased re-export exists solely for
 *   back-compat during the import-site migration and will be removed in a
 *   follow-up.
 */
export { __internal_resetPreloadPromisesForTests as resetPreloadPromisesForTests } from './preload-registry';
